<template>
  <div
    :style="{ textAlign: align, width }"
    :header-name="header.value"
    :class="headerClass"
    class="cell-container"
    @mouseenter="showIcon = true"
    @mouseleave="showIcon = false"
  >
    <v-tooltip color="primary" left :disabled="tooltipDisabled">
      <template v-slot:activator="{ on }">
        <div ref="content" class="content" v-on="on">{{ displayValue }}</div>
      </template>
      <span>{{ displayValue }}</span>
    </v-tooltip>

    <!-- Details Icon -->
    <div v-show="showIcon && false" class="cell-details-icon" title="Open details" @click="openDetailsPopup">
      <v-icon small>mdi-information-outline</v-icon>
    </div>

    <!-- Details Popup Component -->
    <EcsTableCellDetails
      :show="showDetailsPopup"
      :header="header"
      :agent-name="getAgentName()"
      @close="closeDetailsPopup"
    />
  </div>
</template>

<script>
import moment from 'moment-timezone';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';
import EcsTableCellDetails from '@/components/ecs-table/EcsTableCellDetails';
import { COLUMN_TYPE } from '@/constants/table';

export default {
  name: 'EcsTableCell',

  components: {
    EcsTableCellDetails
  },

  mixins: [EcsTimeFormatter],

  props: {
    header: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number, Boolean],
      required: true
    },
    width: {
      type: String,
      required: true
    },
    now: {
      type: Object,
      required: true
    },
    appTimeZoneId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      fromNowInterval: null,
      tooltipDisabled: true,
      showIcon: false,
      showDetailsPopup: false
    };
  },
  computed: {
    align() {
      const fallbackAlignment = this.header.align === 'start' ? 'left' : 'center';
      return this.header.align === 'end' ? 'right' : fallbackAlignment;
    },
    displayValue() {
      if (this.header.type === COLUMN_TYPE.PERCENTAGE) {
        return this.getPercentageValue();
      } else if (this.header.type === COLUMN_TYPE.TIME) {
        return this.getTimeValue();
      } else if (this.header.type === COLUMN_TYPE.DATE) {
        return this.getDateValue();
      } else if (this.header.type === COLUMN_TYPE.TIME_FROM_NOW) {
        return this.getTimeFromNowValue();
      } else {
        return this.value;
      }
    },
    headerClass() {
      const classList = [];
      if (this.header.value === 'currentLoadAllowance') {
        classList.push(this.value !== 'Basic' ? 'highlight' : '');
      }
      return classList.join(' ');
    }
  },
  watch: {
    width() {
      this.disableTooltip();
    }
  },
  beforeDestroy() {
    clearInterval(this.fromNowInterval);
  },
  mounted() {
    this.disableTooltip();
  },
  methods: {
    getPercentageValue() {
      return `${(this.value * 100).toFixed(2)}%`;
    },
    getTimeValue() {
      const duration = moment.duration(this.value, 'seconds');
      return moment.utc(duration.asMilliseconds()).format('HH:mm:ss');
    },
    getDateValue() {
      if (this.value === '0001-01-01T00:00:00.000+00:00') {
        return 'NA';
      }

      return moment(this.value)
        .tz(this.getIanaTimeZone(this.appTimeZoneId))
        .format('MM/DD/YYYY h:mm a');
    },

    getTimeFromNowValue() {
      if (this.value === '0001-01-01T00:00:00.000+00:00') {
        return '00:00:00';
      }
      const end = moment(this.value).tz(this.getIanaTimeZone(this.appTimeZoneId));
      const duration = moment.duration(this.now.diff(end), 'seconds');
      return moment.utc(duration.asSeconds()).format('HH:mm:ss');
    },
    disableTooltip() {
      this.$nextTick(() => {
        this.$forceUpdate();
        const content = this.$refs.content;
        if (content) {
          this.tooltipDisabled = content.offsetWidth >= content.scrollWidth;
        } else {
          this.tooltipDisabled = true;
        }
      });
    },
    openDetailsPopup() {
      this.showDetailsPopup = true;
      // Emit event to notify parent that details popup was opened
      this.$emit('cell-details-opened', {
        header: this.header,
        value: this.value,
        displayValue: this.displayValue
      });
    },
    closeDetailsPopup() {
      this.showDetailsPopup = false;
    },
    getAgentName() {
      // You can customize this logic based on your data structure
      // For now, returning a placeholder or value from the header
      return this.header.agentName || 'Agent Name';
    }
  }
};
</script>

<style scoped>
.v-tooltip__content {
  max-width: 200px;
}
.content {
  text-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cell-container {
  position: relative;
}

.cell-details-icon {
  position: absolute;
  top: 50%;
  right: 5px;
  transform: translateY(-50%);
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 2px;
  transition: all 0.2s ease;
}

.cell-details-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
  transform: translateY(-50%) scale(1.1);
}

.cell-details-icon .v-icon {
  color: #666;
}

.cell-details-icon:hover .v-icon {
  color: #333;
}
</style>
